import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getTrackOrderPath } from '@/constants/Order';
import { getBackgroundLocation } from '@/utilities/RouteUtils';
import type { IBackgroundLocationState } from '@/utilities/RouteUtils';

/**
 * Survives React StrictMode remounting the overlay, which resets refs, so
 * opening pushes a single `/track/:orderId` history entry.
 */
let activeTrackPushPath: string | null = null;

export const resetTrackOrderUrlSyncForTests = () => {
  activeTrackPushPath = null;
};

interface IUseTrackOrderUrlSyncOptions {
  /** Id shown in the `/track/:orderId` url. */
  orderId: string | number;
  /** Set to false to leave the url alone (e.g. the participant search overlay). */
  enabled?: boolean;
  /** Where to go on close when the overlay was opened directly from the url. */
  closeRedirectPath: string;
  /** Closes the overlay when the user leaves `/track/:orderId` via browser back. */
  onLeaveTrackRoute: () => void;
}

/**
 * Keeps the url at `/track/:orderId` while the track order overlay is open.
 *
 * Opening pushes `/track/:orderId` with the current page as `backgroundLocation`
 * so the page keeps rendering under the overlay. Closing pops that entry, or
 * replaces it with `closeRedirectPath` when the overlay was deep-linked.
 */
export const useTrackOrderUrlSync = ({
  orderId,
  enabled = true,
  closeRedirectPath,
  onLeaveTrackRoute,
}: IUseTrackOrderUrlSyncOptions) => {
  const navigate = useNavigate();
  const location = useLocation();
  const trackPath = getTrackOrderPath(orderId);

  const hasSyncedRef = useRef(false);
  const didPushRef = useRef(false);
  const isClosingRef = useRef(false);
  const hasSeenTrackPathRef = useRef(location.pathname === trackPath);
  const onLeaveTrackRouteRef = useRef(onLeaveTrackRoute);
  onLeaveTrackRouteRef.current = onLeaveTrackRoute;

  /** Push `/track/:orderId` once on open, keeping the current page underneath. */
  useEffect(() => {
    if (!enabled || hasSyncedRef.current) {
      return;
    }

    hasSyncedRef.current = true;

    if (location.pathname === trackPath) {
      return;
    }

    // StrictMode runs this effect twice with fresh refs; the first run already pushed.
    if (activeTrackPushPath === trackPath) {
      didPushRef.current = true;
      return;
    }

    const state: IBackgroundLocationState = {
      backgroundLocation: getBackgroundLocation(location) ?? location,
    };

    activeTrackPushPath = trackPath;
    didPushRef.current = true;
    navigate(trackPath, { state });
  }, [enabled, location, navigate, trackPath]);

  /**
   * Browser back/forward moved off the track url while the overlay is open.
   * Ignore the page the overlay was opened from — the push above has not landed yet.
   */
  useEffect(() => {
    if (!enabled || isClosingRef.current) {
      return;
    }

    if (location.pathname === trackPath) {
      hasSeenTrackPathRef.current = true;
      return;
    }

    if (!hasSeenTrackPathRef.current) {
      return;
    }

    isClosingRef.current = true;
    activeTrackPushPath = null;
    onLeaveTrackRouteRef.current();
  }, [enabled, location.pathname, trackPath]);

  const closeTrackOrderUrl = useCallback(() => {
    if (!enabled || isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    activeTrackPushPath = null;

    // Pop when this overlay pushed the track url, or when it was opened on top of a page.
    if (didPushRef.current || getBackgroundLocation(location)) {
      navigate(-1);
      return;
    }

    navigate(closeRedirectPath, { replace: true });
  }, [closeRedirectPath, enabled, location, navigate]);

  return { closeTrackOrderUrl };
};

export default useTrackOrderUrlSync;
